/**
 * Worksheet Layout Constants and Math
 * 
 * This module defines the single source of truth for all layout calculations.
 * Both preview and PDF use these exact same values - no duplication.
 * 
 * All measurements start in INCHES, then convert to PIXELS using DPI.
 */

// Page dimensions in inches (US Letter)
export const PAGE_WIDTH_INCHES = 8.5;
export const PAGE_HEIGHT_INCHES = 11;

// DPI for rendering
export const RENDER_DPI = 300; // High quality for PDF
export const PREVIEW_DPI = 96;  // Screen display

// Convert inches to pixels at given DPI
export const inchesToPixels = (inches, dpi) => inches * dpi;

// Page dimensions in pixels at render DPI
export const PAGE_WIDTH_PX = inchesToPixels(PAGE_WIDTH_INCHES, RENDER_DPI);  // 2550
export const PAGE_HEIGHT_PX = inchesToPixels(PAGE_HEIGHT_INCHES, RENDER_DPI); // 3300

/**
 * Tracing Rectangle Bounds (in inches)
 * Measured from template images - this is the editable area for name tracing
 * 
 * Template layout analysis:
 * - "Name Tracing Practice!" header: ~10% height
 * - "Date:" section: ~15% height  
 * - Tracing Rectangle: starts at ~25% from top, ends at ~65%
 * 
 * For 11 inch page: starts at 2.75", height is ~4.4"
 */
export const TRACING_RECT = {
  // Left margin from page edge (aligns with rectangle border)
  left: 0.38,
  // Top margin - where the rectangle STARTS (not the border, the inside)
  top: 2.85,
  // Width of usable tracing area
  width: 7.74,
  // Height of usable tracing area  
  height: 4.35,
  // Inner padding from the rectangle edges
  padding: 0.15
};

// Inner bounds (content area inside padding)
export const TRACING_INNER = {
  left: TRACING_RECT.left + TRACING_RECT.padding,
  top: TRACING_RECT.top + TRACING_RECT.padding,
  width: TRACING_RECT.width - (TRACING_RECT.padding * 2),
  height: TRACING_RECT.height - (TRACING_RECT.padding * 2)
};

// Number of tracing rows
export const ROW_COUNT = 3;

// Stroke widths in inches
export const LINE_STROKE_WIDTH = 0.015; // ~4.5px at 300 DPI

/**
 * Calculate row layout with equal vertical distribution
 * Rows are centered in the rectangle with equal margins above and below
 * Returns array of row objects with guide line positions
 */
export const calculateRows = () => {
  const rows = [];
  
  // Available height inside the tracing rectangle
  const availableHeight = TRACING_INNER.height;
  
  // Line height for each row (the space between top and bottom guide lines)
  // Use 75% of available height divided by 3 rows for the actual line areas
  const lineAreaHeight = (availableHeight * 0.75) / ROW_COUNT;
  
  // Spacing between rows
  const rowSpacing = lineAreaHeight * 0.35;
  
  // Total height used by rows and spacing
  const totalRowsHeight = (lineAreaHeight * ROW_COUNT) + (rowSpacing * (ROW_COUNT - 1));
  
  // Equal margin above first row and below last row (centered)
  const verticalMargin = (availableHeight - totalRowsHeight) / 2;
  
  // Additional offset to lower rows (100px at 300 DPI)
  const rowOffset = 100 / RENDER_DPI;
  
  for (let i = 0; i < ROW_COUNT; i++) {
    // Calculate the top of this row's line area (with offset)
    const rowTop = TRACING_INNER.top + verticalMargin + rowOffset + (i * (lineAreaHeight + rowSpacing));
    
    // Position lines within the row
    const topLineY = rowTop;
    const bottomLineY = rowTop + lineAreaHeight;
    const midLineY = (topLineY + bottomLineY) / 2;
    
    rows.push({
      index: i,
      // Row bounds (for debugging)
      top: rowTop,
      height: lineAreaHeight,
      // Guide lines (Y position in inches)
      topLine: topLineY,
      midLine: midLineY,
      bottomLine: bottomLineY,
      // Line height for text sizing
      lineHeight: bottomLineY - topLineY,
      // X positions for text/lines
      left: TRACING_INNER.left,
      width: TRACING_INNER.width
    });
  }
  
  return rows;
};

// Pre-calculate rows
export const ROWS = calculateRows();

/**
 * Font sizing calculations
 * 
 * For tracing fonts, we want:
 * - Baseline sitting on bottom line
 * - Lowercase x-height reaching midline
 * - Uppercase cap height touching top line
 * 
 * Font size = cap height (in points) where 1pt = 1/72 inch
 */
export const calculateFontSize = (lineHeight) => {
  // lineHeight is the distance from top line to bottom line in inches
  // Cap height is approximately 70% of em for most fonts
  // So we need fontSize where capHeight = lineHeight
  // fontSize * 0.7 = lineHeight (in inches)
  // fontSize = lineHeight / 0.7
  
  // Convert inches to points (1 inch = 72 points)
  const fontSizePoints = (lineHeight / 0.7) * 72;
  return fontSizePoints;
};

// Default font size based on row line height
export const DEFAULT_FONT_SIZE = calculateFontSize(ROWS[0].lineHeight);

// Minimum font size (for preschoolers - must be large)
export const MIN_FONT_SIZE = 24; // points - about 0.33 inches cap height

/**
 * Auto-fit calculation
 * Given a name and available width, return the font size that fits
 * 
 * @param {string} name - The name to fit
 * @param {number} availableWidth - Width in inches
 * @param {function} measureText - Function that measures text width given (text, fontSize)
 * @returns {number} - Font size in points
 */
export const calculateAutoFitFontSize = (name, availableWidth, measureText) => {
  if (!name || !name.trim()) {
    return DEFAULT_FONT_SIZE;
  }
  
  let fontSize = DEFAULT_FONT_SIZE;
  
  // Measure at default size
  let textWidth = measureText(name, fontSize);
  const availableWidthPx = inchesToPixels(availableWidth, RENDER_DPI);
  
  // If text fits, use default
  if (textWidth <= availableWidthPx) {
    return fontSize;
  }
  
  // Scale down to fit
  const scale = availableWidthPx / textWidth;
  fontSize = Math.floor(fontSize * scale);
  
  // Enforce minimum
  return Math.max(fontSize, MIN_FONT_SIZE);
};

/**
 * Get layout in pixels at specified DPI
 */
export const getLayoutInPixels = (dpi = RENDER_DPI) => {
  const scale = dpi / RENDER_DPI;
  
  return {
    page: {
      width: PAGE_WIDTH_PX * scale,
      height: PAGE_HEIGHT_PX * scale
    },
    tracingRect: {
      left: inchesToPixels(TRACING_RECT.left, dpi),
      top: inchesToPixels(TRACING_RECT.top, dpi),
      width: inchesToPixels(TRACING_RECT.width, dpi),
      height: inchesToPixels(TRACING_RECT.height, dpi),
      padding: inchesToPixels(TRACING_RECT.padding, dpi)
    },
    rows: ROWS.map(row => ({
      ...row,
      topPx: inchesToPixels(row.top, dpi),
      heightPx: inchesToPixels(row.height, dpi),
      topLinePx: inchesToPixels(row.topLine, dpi),
      midLinePx: inchesToPixels(row.midLine, dpi),
      bottomLinePx: inchesToPixels(row.bottomLine, dpi),
      leftPx: inchesToPixels(row.left, dpi),
      widthPx: inchesToPixels(row.width, dpi),
      lineHeightPx: inchesToPixels(row.lineHeight, dpi)
    })),
    lineStrokeWidth: inchesToPixels(LINE_STROKE_WIDTH, dpi)
  };
};

// Templates configuration
export const TEMPLATES = [
  {
    id: 'template1',
    name: 'Hollydog Loves You',
    localPath: '/templates/template1.png',
    description: 'Smiley face and Dog'
  },
  {
    id: 'template2', 
    name: 'Like the Way You Think',
    localPath: '/templates/template2.png',
    description: 'Star and Happy Dog'
  },
  {
    id: 'template3',
    name: "There's Only One You",
    localPath: '/templates/template3.png',
    description: 'Heart and Dog with glasses'
  }
];
